import { StFeatureFlagFlags } from '@/plugins/rollout';
import { createApi } from '@stilingue/st-auth-utils';
import Cookies from 'js-cookie';
import Service from '@/services/Service';
import { error401Handler } from '@/utils/error';
import { readSessionStorageCache, writeSessionStorageCache } from '@/utils/sessionStorageCache';

const urlConfig = {
  production: {
    version: StFeatureFlagFlags.UserLegacyServiceVersionProd.getValue(),
    gateway: StFeatureFlagFlags.gatewayProd.getValue(),
  },
  staging: {
    version: StFeatureFlagFlags.UserLegacyServiceVersionStaging.getValue(),
    gateway: StFeatureFlagFlags.gatewayStaging.getValue(),
  },
};

const afterParser = response => response.data;
const CACHE_VERSION = 1;
const CACHE_KEY_PREFIX = 'accounts-and-permissions';
const CACHE_TTL_MS = 60 * 60 * 1000;
const CACHE_WARNING = '[AccountsAndPermissionsService] Cache unavailable; fetching response.';

const getCacheKey = (environment, userIdentity) =>
  `${CACHE_KEY_PREFIX}:v${CACHE_VERSION}:${environment}:${encodeURIComponent(userIdentity)}`;

const getFrontendRelevantData = responseData => (Array.isArray(responseData) ? responseData : [])
  .filter(item => item && item.account && typeof item.account === 'object')
  .map(({ account }) => ({
    account: {
      id: account.id,
      name: account.name,
      universeIds: account.universeIds,
    },
  }));

const isValidCachedAccount = account => account &&
  typeof account === 'object' &&
  !Array.isArray(account) &&
  Object.keys(account).every(key => ['id', 'name', 'universeIds'].includes(key));

const isValidCachedEntry = item => item &&
  typeof item === 'object' &&
  !Array.isArray(item) &&
  Object.keys(item).length === 1 &&
  Object.prototype.hasOwnProperty.call(item, 'account') &&
  isValidCachedAccount(item.account);

const isValidCachedData = data => Array.isArray(data) && data.every(isValidCachedEntry);

export default class AccountsAndPermissionsService extends Service {
  constructor(isStaging) {
    const { version, gateway } = isStaging ? urlConfig.staging : urlConfig.production;
    const api = createApi(`upms-legacy/${version}/user/`, gateway, { error401Handler });
    super(api, { afterParser });
    this.environment = isStaging ? 'staging' : 'production';
  }

  async getAccountsAndPermissions(userIdentity) {
    const cacheKey = userIdentity ? getCacheKey(this.environment, userIdentity) : null;

    const cachedResponse = cacheKey && readSessionStorageCache({
      cacheKey,
      version: CACHE_VERSION,
      isValidData: isValidCachedData,
      warningMessage: CACHE_WARNING,
    });
    if (cachedResponse) return getFrontendRelevantData(cachedResponse);

    const responseData = await this.find({
      url: 'accounts-and-permissions',
      headers: {
        'stilingue-session-id': Cookies.get('stilingue-session-id'),
      },
    });

    const frontendRelevantData = getFrontendRelevantData(responseData);

    if (cacheKey) {
      writeSessionStorageCache({
        cacheKey,
        version: CACHE_VERSION,
        ttlMs: CACHE_TTL_MS,
        data: frontendRelevantData,
        warningMessage: CACHE_WARNING,
      });
    }

    return frontendRelevantData;
  }
}
